
import React, { useCallback, useEffect, useState } from 'react';
import styles from './VideoPreviewDialog.module.scss';
import { useTranslation } from 'react-i18next';
import HLSVideoPlayer from './HLSVideoPlayer';

interface VideoPreviewDialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    videoUrl: string;
    isYouTube?: boolean;
}

const VideoPreviewDialog: React.FC<VideoPreviewDialogProps> = ({
    open,
    onClose,
    title,
    videoUrl,
    isYouTube = false
}) => {
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && !isMobile) {
            onClose();
        }
    }, [onClose, isMobile]);

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [open]);

    if (!open) return null;

    const getYouTubeEmbedUrl = (url: string) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        const videoId = match && match[2].length === 11 ? match[2] : null;
        return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : url;
    };

    return (
        <div className={`${styles.dialogOverlay} ${isMobile ? styles.mobileOverlay : ''}`} onClick={handleBackdropClick}>
            <div className={`${styles.dialogContainer} ${isMobile ? styles.mobileContainer : ''}`}>
                <div className={`${styles.dialogHeader} ${isMobile ? styles.mobileHeader : ''}`}>
                    {!isMobile && <h2 className={styles.dialogTitle}>{title}</h2>}
                    <button
                        className={`${styles.closeButton} ${isMobile ? styles.mobileCloseButton : ''}`}
                        onClick={onClose}
                        aria-label={t('common.close')}
                    >
                        ✕
                    </button>
                </div>
                <div className={`${styles.dialogContent} ${isMobile ? styles.mobileContent : ''}`}>
                    <div className={styles.videoContainer}>
                        {isYouTube ? (
                            <iframe
                                className={styles.video}
                                src={getYouTubeEmbedUrl(videoUrl)}
                                title={t('video.preview', 'Видео превью')}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        ) : (
                            <HLSVideoPlayer
                                className={styles.video}
                                src={videoUrl}
                                controls
                                autoPlay
                                playsInline
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoPreviewDialog;